import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import type { BaseQLFieldDefinition } from '@adornis/baseql/metadata/types.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { xComponents } from '@adornis/chemistry/elements/x-components.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

type DisplayOptions = Partial<{
  useTextarea: boolean;
}>;

const displayOptionRegistry = {} as Record<string, DisplayOptions>;
export function DisplayOptions(fieldOptions: DisplayOptions) {
  return (target: AdornisEntity, propertyName: string) => {
    // console.log('register -----------------------------------------', target);
    displayOptionRegistry[(target.constructor as any)._class + '.' + propertyName] = fieldOptions;
  };
}

/**
 * pass in .document=${yourEntity}
 *
 * fires ~@success and ~@cancel with the current version of the document in e.detail.value
 *
 * @deprecated use the functional GeneratedForm from @adornis/generated-form instead
 *
 * @element generated-form
 */
@customElement('bh-plugin-generated-form')
export class BhPluginGeneratedForm<T extends AdornisEntity> extends ChemistryLitElement {
  @property({ attribute: false }) formController?: any; // GenericFieldFormController
  @property({ type: Boolean }) controls?: boolean;
  @property({ type: Boolean }) heading?: boolean;
  @property({ attribute: false }) blacklist: string[] = [];
  @property({ attribute: false }) whitelist: string[] = [];
  @property({ attribute: true }) layout = 'field' as 'field' | 'key-field';
  @property({ attribute: true }) columns = '1';

  @property({ attribute: 'placeholder-mode' }) placeholderMode = 'inline-floating';

  @state() t = new TranslationController(this);

  override render() {
    if (!this.formController) return html` <x-text>Es muss ein FormController angegeben werden</x-text>`;
    // if (!(this.formController instanceof GenericFieldFormController))
    //   throw new Error('please pass a GenericFieldFormController instead of a normal FormController');
    if (!this.formController.document)
      return html` <x-text> Der Formcontroller muss ein Dokument beinhalten </x-text> `;
    if (!this.controls) return xComponents(this._generateFieldsForEntity(this.formController.document));
    return xComponents(html`
      <x-flex space="sm">
        ${
          this.heading
            ? html`
                <x-h2>
                  ${this.formController.document._class}
                  ${!this.formController.document._id ? 'erstellen' : 'bearbeiten'}
                </x-h2>
              `
            : nothing
        }
        ${this._generateFieldsForEntity(this.formController.document)}
        <x-flex horizontal end space="md">
          <x-button
            mode="outline"
            tone="error"
            icon="delete"
            @click=${() =>
              XDialog.guard(async () => {
                await (this.formController?.document?.remove() as void | Promise<any>);
                this.dispatchEvent(new CustomEvent('cancel', { detail: { value: this.formController?.document } }));
              })}
          >
            löschen
          </x-button>
          <x-button
            mode="outline"
            @click=${() =>
              this.dispatchEvent(new CustomEvent('cancel', { detail: { value: this.formController?.document } }))}
            icon="close"
          >
            Abbrechen
          </x-button>
          <x-button icon="check" @click=${() => this.formController?.validate()}> Speichern </x-button>
        </x-flex>
      </x-flex>
    `);
  }

  private _generateFieldsForEntity(entity: Maybe<T>, path: string[] = [], maxDepth = 5) {
    if (!entity || maxDepth <= 0) return nothing;

    let fields = Object.entries(entity.fields);
    fields = fields.filter(([name]) => !['_class', '_id'].includes(name));
    fields = fields.filter(([name, field]) => !field.options.resolve);

    if (this.whitelist.length > 0) {
      fields = fields.filter(([name]) => this.whitelist.some(whitelisted => [...path, name].includes(whitelisted)));
    } else {
      fields = fields.filter(([name]) => !this.blacklist.some(blacklisted => [...path, name].includes(blacklisted)));
    }

    return html`
      <x-grid
        space="sm"
        columns=${+this.columns * (this.layout === 'key-field' ? 2 : 1)}
        ${css({ alignItems: 'center' })}
      >
        ${fields.map(([name, fieldDef]) => {
          const arrayDepth = fieldDef.arrayDepth();
          if (arrayDepth) {
            if (arrayDepth > 1) throw new Error('Cannot handle more than 1D arrays');

            return this.renderArrayField(entity, name, path, fieldDef);
          }

          const pathToName = [...path, name] as [any];

          const type = fieldDef.type();
          if ((type as any)._isBaseQLEnum) return this.renderField({ host: this, fieldDef, pathToName });
          const scalar = Object.values(baseqlMetaData.scalars).find(s => s.isTypeOf(type));
          if (scalar) return this.renderField({ host: this, fieldDef, pathToName });
          if (new (type as any)({}) instanceof AdornisEntity) {
            if (!entity[name]) entity[name] = new (type as any)({});
            return this._generateFieldsForEntity(entity[name], pathToName, maxDepth - 1);
          }

          return this.renderField({ host: this, fieldDef, pathToName });
        })}
        <slot></slot>
      </x-grid>
    `;
  }

  public renderField(options: {
    host: BhPluginGeneratedForm<any>;
    fieldDef: BaseQLFieldDefinition<any>;
    pathToName: [any];
  }) {
    const displayOptions = (() => {
      if (!this.formController) return null;
      const key = this.formController.document._class + '.' + options.fieldDef.name;
      // console.log({ displayOptionRegistry, key });
      return displayOptionRegistry[key];
    })();
    console.log('render Field', options, displayOptions);

    const field = (() => {
      if (displayOptions?.useTextarea) {
        return html`
          <x-textarea
            placeholder=${this.t.translate('field', options.fieldDef.name as any)}
            ${this.formController?.field(options.pathToName as string[])}
          ></x-textarea>
        `;
      }

      return this.formController?.genericField(options.pathToName as string[], {
        placeholderMode: this.placeholderMode,
        plainField: this.layout === 'key-field',
      });
    })();

    return this.layout === 'key-field'
      ? [html` <x-text align="right" tone="subtle"> ${options.fieldDef.name} </x-text> `, field]
      : field;
  }

  private renderArrayField(entity: T, fieldName: string, pathToField: string[], fieldDef: BaseQLFieldDefinition<any>) {
    // Array is not initialised. Set default value to empty array
    if (!entity[fieldName]) entity[fieldName] = [];

    return html`
      <x-basic-card space="sm" padding="md">
        <x-h3>${this.t.translate('field', fieldName as any)} (${entity[fieldName]?.length ?? 0})</x-h3>
        ${repeat(entity[fieldName] ?? [], (entry, index) => {
          return html`
            <x-flex horizontal space="sm">
              <x-icon
                ${css({
                  color: this.colors.tone.error,
                  fontSize: '1.5em',
                  cursor: 'pointer',
                  padding: '.25em',
                  borderRadius: this.sizes.borderRadiusSecondary,
                  width: 'min-content',
                  height: 'min-content',
                  margin: '0 auto',
                  transition: '200ms',
                })}
                ${acss({
                  '&:hover': {
                    background: this.colors.tone.error,
                    color: `${this.colors.tone.whitePrimary} !important`,
                  },
                })}
                tone="error"
                @click=${() => {
                  entity[fieldName].splice(index, 1);
                  this.requestUpdate();
                }}
              >
                delete
              </x-icon>
              <x-flex space="sm" flex>
                ${
                  entry instanceof AdornisEntity
                    ? this._generateFieldsForEntity(entry as T, [...pathToField, fieldName, index.toString()])
                    : this.renderField({
                        host: this,
                        fieldDef,
                        pathToName: [...pathToField, fieldName, index] as unknown as [any],
                      })
                }
              </x-flex>
            </x-flex>
          `;
        })}
        <x-button
          mode="outline"
          icon="add"
          @click=${async () => {
            try {
              const constr = fieldDef.type() as typeof AdornisEntity;
              const possibleSubclasses = baseqlMetaData.entities
                .resolveNode(constr._class)
                .children.map(c => c.type()) as Array<typeof AdornisEntity> | undefined;
              const chosenConstr = !possibleSubclasses?.length
                ? constr
                : possibleSubclasses.length === 1
                  ? possibleSubclasses[0]
                  : await XDialog.showPopup<typeof AdornisEntity>({
                      content: choose => html`
                        <x-flex padding="md" space="sm">
                          ${possibleSubclasses.map(
                            c => html`
                              <x-button mode="outline" icon="add" @click=${() => choose(c)}>
                                ${this.t.translate('class', c._class as any)}
                              </x-button>
                            `,
                          )}
                        </x-flex>
                      `,
                    });
              if (!chosenConstr) return;
              // @ts-expect-error sure, but we have subclasses here
              let newField = new chosenConstr({});
              if (newField instanceof String) newField = '';
              entity[fieldName].push(newField);
              this.requestUpdate();
            } catch (err) {
              logger.error(err);
            }
          }}
        >
          ${this.t.translate('cms', 'addEntry' as any)}
        </x-button>
      </x-basic-card>
    `;
  }
}
